import AssistantsApi from '@/api/assistants';
import AssistantModel from '@/models/assistant';

const getInitialParams = () => ({
  skip: 0,
  limit: 100,
  only_deleted: false,
  order: {
    column: 'title',
    desc: true,
  },
});

export default {
  namespaced: true,

  state: {
    data: [],
    meta: {},
    statuses: [],
    loading: false,
    error: null,
    params: getInitialParams(),
    currentItem: null,
  },

  actions: {
    async fetch(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AssistantsApi.fetch(ctx.getters.params);

        if (!response.cancelled) {
          ctx.commit('setData', response.data.data.items);
          ctx.commit('setMeta', response.data.meta);
          ctx.commit('setLoading', false);
        }
      } catch (err) {
        ctx.commit('setError', err);
        ctx.commit('setLoading', false);
      }
    },

    async getConfig(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AssistantsApi.getConfig();
        ctx.commit('setStatuses', response.data.data.statuses);
        ctx.commit('setMeta', response.data.meta);
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async fetchOne(ctx, id) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AssistantsApi.fetchOne(id);
        ctx.commit('setCurrentItem', new AssistantModel(response.data.data));
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async save(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AssistantsApi.update(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async remove(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        await AssistantsApi.remove(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async create(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AssistantsApi.create(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async changeParams(ctx, params, reset) {
      if (reset) {
        ctx.commit('changeParams', {
          ...getInitialParams(),
          ...params,
        });
      } else {
        ctx.commit('changeParams', params);
      }
    },

    clearCurrentItem(ctx) {
      ctx.commit('clearCurrentItem');
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, err) {
      state.error = err;
    },

    setData(state, data) {
      state.data = data;
    },

    setMeta(state, meta) {
      state.meta = meta;
    },

    setStatuses(state, data) {
      state.statuses = data;
    },

    changeParams(state, params) {
      Object.assign(state.params, params);
    },

    setCurrentItem(state, data) {
      state.currentItem = data;
    },

    clearCurrentItem(state, data) {
      state.currentItem = null;
    },
  },

  getters: {
    meta: state => state.meta,
    list: state => state.data,
    statuses: state => state.statuses,
    error: state => state.error,
    params: (state, getters) => {
      return {
        ...state.params,
        search_string: getters.searchQuery,
        order_by: state.params.order.column,
        order_dir: state.params.order.desc ? 'desc' : 'asc',
      };
    },
    loading: state => state.loading,
    searchQuery: (state, getters, rootState, rootGetters) => rootGetters['util/searchQuery'],
    currentItem: state => state.currentItem,
  },
};
